<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

// Animated count-up for KPI values
const animatedValues = ref({});

function animateValue(key, target) {
  if (typeof target !== 'number' || isNaN(target)) {
    animatedValues.value[key] = target;
    return;
  }
  const start = animatedValues.value[key] ?? 0;
  if (typeof start !== 'number') {
    animatedValues.value[key] = 0;
  }
  const from = typeof animatedValues.value[key] === 'number' ? animatedValues.value[key] : 0;
  if (from === target) return;

  const duration = 800;
  const startTime = performance.now();

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function tick(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeOutCubic(progress);
    animatedValues.value[key] = Math.round(from + (target - from) * eased);
    if (progress < 1) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}



const props = defineProps({
  summary: {
    type: Object,
    default: () => ({ current: {}, previous: {} }),
  },
});

const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const agentStatus = useMapGetter('agents/getAgentStatus');

function pctChange(metricKey) {
  const cur = props.summary.current?.[metricKey];
  const prev = props.summary.previous?.[metricKey];
  if (cur == null && prev == null) return { pct: 0, up: true };
  if (prev === 0 || prev == null) return { pct: 100, up: true };
  if (cur == null) return { pct: 100, up: false };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}

const kpiCards = computed(() => [
  {
    key: 'open',
    label: 'Conversas abertas',
    value: accountConversationMetric.value?.open ?? '-',
    icon: 'chat',
    summaryKey: 'conversations_count',
  },
  {
    key: 'unattended',
    label: 'Nao atendidas',
    value: accountConversationMetric.value?.unattended ?? '-',
    icon: 'chat',
    summaryKey: 'conversations_count',
  },
  {
    key: 'unassigned',
    label: 'Nao atribuidas',
    value: accountConversationMetric.value?.unassigned ?? '-',
    icon: 'chat',
    summaryKey: 'conversations_count',
  },
  {
    key: 'pending',
    label: 'Pendentes',
    value: accountConversationMetric.value?.pending ?? '-',
    icon: 'clock',
    summaryKey: 'resolutions_count',
  },
  {
    key: 'online',
    label: 'Agentes disponiveis',
    value: agentStatus.value?.online ?? '-',
    icon: 'user',
    summaryKey: null,
  },
  {
    key: 'busy',
    label: 'Agentes ocupados',
    value: agentStatus.value?.busy ?? '-',
    icon: 'user',
    summaryKey: null,
  },
  {
    key: 'offline',
    label: 'Agentes offline',
    value: agentStatus.value?.offline ?? '-',
    icon: 'user',
    summaryKey: null,
  },
]);

// Watch kpiCards and animate values
watch(kpiCards, (cards) => {
  cards.forEach(card => {
    if (card.summaryKey) {
      const { pct } = pctChange(card.summaryKey);
      animateValue('pct_' + card.key, pct);
    }
  });
}, { immediate: true, deep: true });

function getSubtitle(card) {
  if (!card.summaryKey) return { text: 'Em tempo real', pct: null, up: null };
  const { pct, up } = pctChange(card.summaryKey);
  // Use animated pct
  const animKey = 'pct_' + card.key;
  const animPct = animatedValues.value[animKey];
  const displayPct = (typeof animPct === 'number') ? animPct : pct;
  if (up) return { text: displayPct + '% a mais que o mes anterior', pct, up: true };
  return { text: displayPct + '% a menos que o mes anterior', pct, up: false };
}
</script>

<template>
  <div class="sz-kpi-row">
    <div
      v-for="card in kpiCards"
      :key="card.key"
      class="sz-kpi-card"
    >
      <div class="sz-kpi-header">
        <span class="sz-kpi-icon">
          <svg v-if="card.icon === 'chat'" width="20" height="20" viewBox="0 0 17 16" fill="none">
            <path d="M15 3.75H12.5V1.25C12.5 0.918479 12.3683 0.600537 12.1339 0.366116C11.8995 0.131696 11.5815 0 11.25 0H1.25C0.918479 0 0.600537 0.131696 0.366116 0.366116C0.131696 0.600537 0 0.918479 0 1.25V11.25C0.000366963 11.3676 0.0338962 11.4827 0.096736 11.5821C0.159576 11.6814 0.249177 11.7611 0.355245 11.8118C0.461314 11.8626 0.579547 11.8824 0.696363 11.869C0.813178 11.8555 0.923836 11.8094 1.01562 11.7359L3.75 9.53125V11.875C3.75 12.2065 3.8817 12.5245 4.11612 12.7589C4.35054 12.9933 4.66848 13.125 5 13.125H12.3117L15.2344 15.4859C15.345 15.5754 15.4828 15.6244 15.625 15.625C15.7908 15.625 15.9497 15.5592 16.0669 15.4419C16.1842 15.3247 16.25 15.1658 16.25 15V5C16.25 4.66848 16.1183 4.35054 15.8839 4.11612C15.6495 3.8817 15.3315 3.75 15 3.75ZM3.32422 8.26406L1.25 9.94141V1.25H11.25V8.125H3.71719C3.57414 8.12502 3.43544 8.1741 3.32422 8.26406ZM15 13.6914L12.9258 12.0141C12.8152 11.9246 12.6774 11.8756 12.5352 11.875H5V9.375H11.25C11.5815 9.375 11.8995 9.2433 12.1339 9.00888C12.3683 8.77446 12.5 8.45652 12.5 8.125V5H15V13.6914Z" fill="currentColor"/>
          </svg>
          <svg v-if="card.icon === 'clock'" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.5" fill="none"/>
            <path d="M10 5v5l3.5 1.75" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
          </svg>
          <svg v-if="card.icon === 'user'" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M18.041 16.563C16.851 14.506 15.017 13.031 12.877 12.331C13.936 11.701 14.758 10.741 15.218 9.598C15.678 8.456 15.75 7.194 15.424 6.006C15.097 4.818 14.389 3.771 13.409 3.024C12.43 2.277 11.232 1.873 10 1.873C8.768 1.873 7.57 2.277 6.591 3.024C5.611 3.771 4.903 4.818 4.577 6.006C4.25 7.194 4.322 8.456 4.782 9.598C5.242 10.741 6.064 11.701 7.123 12.331C4.983 13.03 3.149 14.505 1.959 16.563C1.916 16.634 1.887 16.713 1.874 16.796C1.862 16.878 1.866 16.962 1.886 17.043C1.907 17.124 1.943 17.2 1.994 17.267C2.044 17.333 2.107 17.389 2.18 17.431C2.252 17.472 2.332 17.499 2.415 17.51C2.497 17.52 2.581 17.514 2.662 17.491C2.742 17.468 2.817 17.43 2.882 17.378C2.947 17.326 3.002 17.261 3.041 17.188C4.513 14.644 7.115 13.125 10 13.125C12.885 13.125 15.487 14.644 16.959 17.188C16.998 17.261 17.053 17.326 17.118 17.378C17.183 17.43 17.258 17.468 17.338 17.491C17.419 17.514 17.503 17.52 17.585 17.51C17.668 17.499 17.748 17.472 17.82 17.431C17.893 17.389 17.956 17.333 18.006 17.267C18.057 17.2 18.093 17.124 18.114 17.043C18.134 16.962 18.138 16.878 18.126 16.796C18.113 16.713 18.084 16.634 18.041 16.563ZM5.625 7.5C5.625 6.635 5.882 5.789 6.362 5.07C6.843 4.35 7.526 3.789 8.326 3.458C9.125 3.127 10.005 3.04 10.854 3.209C11.702 3.378 12.482 3.795 13.094 4.407C13.705 5.018 14.122 5.798 14.291 6.647C14.46 7.495 14.373 8.375 14.042 9.174C13.711 9.974 13.15 10.657 12.431 11.138C11.711 11.619 10.865 11.875 10 11.875C8.84 11.874 7.728 11.413 6.908 10.592C6.088 9.772 5.626 8.66 5.625 7.5Z" fill="currentColor"/>
          </svg>
        </span>
        <span class="sz-kpi-label">{{ card.label }}</span>
      </div>

      <span class="sz-kpi-value">{{ card.value }}</span>

      <div
        class="sz-kpi-subtitle"
        :class="{
          'sz-kpi-down': getSubtitle(card).up === false,
          'sz-kpi-neutral': getSubtitle(card).up === null
        }"
      >
        <span>{{ getSubtitle(card).text }}</span>
        <svg v-if="getSubtitle(card).up === true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4.5 11.5L11.5 4.5M11.5 4.5H5.5M11.5 4.5V10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-if="getSubtitle(card).up === false" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4.5 4.5L11.5 11.5M11.5 11.5H5.5M11.5 11.5V5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-kpi-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 16px;
  width: 100%;
}

.sz-kpi-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  gap: 12px;
  min-height: 124px;
  flex: 1;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
}

.sz-kpi-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 24px;
}

.sz-kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #525252;
  flex-shrink: 0;
}

.sz-kpi-label {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #262626;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.sz-kpi-value {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #11361A;
  font-variant-numeric: tabular-nums;
}

.sz-kpi-subtitle {
  margin-top: auto;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: #5EA500;
}

.sz-kpi-subtitle span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.sz-kpi-subtitle svg { flex-shrink: 0; }
.sz-kpi-subtitle.sz-kpi-down { color: #DC2626; }
.sz-kpi-subtitle.sz-kpi-neutral { color: #6B7280; }

@media (max-width: 1400px) {
  .sz-kpi-row { flex-wrap: wrap; }
  .sz-kpi-card { min-width: calc(25% - 12px); }
}
@media (max-width: 1024px) {
  .sz-kpi-card { min-width: calc(33.33% - 11px); }
}
@media (max-width: 768px) {
  .sz-kpi-card { min-width: calc(50% - 8px); }
  .sz-kpi-label { font-size: 13px; }
}
</style>

<style>
/* Dark mode */
.dark .sz-kpi-card { background: #1F2125; border-color: #363840; }
.dark .sz-kpi-icon { color: #9CA3AF; }
.dark .sz-kpi-label { color: #D4D4D4; }
.dark .sz-kpi-value { color: #E5E7EB; }
.dark .sz-kpi-subtitle { color: #7CCF3A; }
.dark .sz-kpi-subtitle.sz-kpi-down { color: #F87171; }
.dark .sz-kpi-subtitle.sz-kpi-neutral { color: #9CA3AF; }
</style>
